// SessionMenu — kebab (⋮) dropdown for a single session row.
//
// Used by both the SessionsRail row and the SearchResults row. One menu
// shape with a drill-down for "Move to project" so sub-navigation works
// identically on desktop and mobile. Only two top-level actions:
//
//   - Move to project →   (opens the project-picker drill-down)
//   - Archive / Unarchive (direct action, calls onPatch, menu closes)
//
// User-facing delete is intentionally absent. Hard delete lives at the
// admin layer (GDPR anonymize path); archive is reversible via the
// search panel's "Include archived" toggle.
//
// Close rules mirror UserMenu: outside-click + Escape. Keyboard focus is
// returned to the trigger on close so the row remains tabbable.
import { useEffect, useRef, useState } from 'react';
import type { CoachProject, CoachSession } from '@gc-coach/shared';
import type { PatchCoachSessionRequest } from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import styles from './Coach.module.css';

export interface SessionMenuProps {
  session: CoachSession;
  projects: readonly CoachProject[];
  /** Called with the patch body — caller performs the HTTP PATCH and the
   *  local state refresh. Keeping the menu side-effect-free lets the row
   *  stay dumb and the parent own the sessions list. */
  onPatch: (patch: PatchCoachSessionRequest) => void;
}

type View = 'main' | 'projects';

export function SessionMenu({ session, projects, onPatch }: SessionMenuProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('main');
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Reset to main view whenever the menu is reopened.
  useEffect(() => {
    if (open) setView('main');
  }, [open]);

  function close() {
    setOpen(false);
  }

  function fire(patch: PatchCoachSessionRequest) {
    onPatch(patch);
    close();
  }

  const archived = session.archived === true;
  const pinned = session.pinned === true;
  const currentProjectId = session.projectId ?? null;

  return (
    <div className={styles.kebabWrap} ref={wrapperRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.kebab}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('history.actions.menuLabel')}
        onClick={(e) => {
          // Row click would select the session — stop it from firing when
          // the user is only going for the menu.
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        <span aria-hidden="true">⋮</span>
      </button>

      {open && (
        <div
          className={styles.menu}
          role="menu"
          // Clicks inside the menu never bubble up to the row.
          onClick={(e) => e.stopPropagation()}
        >
          {view === 'main' && (
            <>
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={() => fire({ pinned: !pinned })}
              >
                {pinned
                  ? t('history.actions.unpin')
                  : t('history.actions.pin')}
              </button>
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={() => setView('projects')}
              >
                <span>{t('projects.move')}</span>
                <span className={styles.menuItemCue} aria-hidden="true">›</span>
              </button>
              {archived ? (
                <button
                  type="button"
                  role="menuitem"
                  className={styles.menuItem}
                  onClick={() => fire({ archived: false })}
                >
                  {t('history.actions.unarchive')}
                </button>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  className={styles.menuItem}
                  onClick={() => fire({ archived: true })}
                >
                  {t('history.actions.archive')}
                </button>
              )}
            </>
          )}

          {view === 'projects' && (
            <>
              <button
                type="button"
                role="menuitem"
                className={`${styles.menuItem} ${styles.menuBack}`}
                onClick={() => setView('main')}
              >
                <span className={styles.menuItemCue} aria-hidden="true">‹</span>
                <span>{t('history.actions.back')}</span>
              </button>
              {currentProjectId !== null && (
                <button
                  type="button"
                  role="menuitem"
                  className={styles.menuItem}
                  onClick={() => fire({ projectId: null })}
                >
                  {t('projects.remove')}
                </button>
              )}
              {projects.length === 0 && (
                <div className={styles.menuEmpty}>
                  {t('history.actions.noProjects')}
                </div>
              )}
              {projects.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="menuitem"
                  className={`${styles.menuItem} ${
                    p.id === currentProjectId ? styles.menuItemCurrent : ''
                  }`}
                  onClick={() =>
                    p.id === currentProjectId ? close() : fire({ projectId: p.id })
                  }
                  disabled={p.id === currentProjectId}
                >
                  <span className={styles.menuProjectName}>◇ {p.name}</span>
                  {p.id === currentProjectId && (
                    <span className={styles.menuItemCue} aria-hidden="true">✓</span>
                  )}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
